import { fetchApiGraphQL } from "@/utils/fetcher";
import { createEnrichmentIsland } from "@/utils/islands";

/* The yes/no buttons in any action email point here:
     /page?token=ZZZ&answer=true&action=openHouseConfirmation
   token authorises the mutation on its own - the API accepts these anonymously and the
   token carries the user plus whatever the action is about, so nothing else reaches the
   mutation. Actions about an advert add &advertId=XXX, which only the server reads, to
   show which residence was answered for. */

type EmailAction = {
    /** The API mutation, which always takes the token plus a single boolean. */
    mutation: string;
    /** What that boolean is called on this mutation. */
    answerArg: string;
};

// Adding an action here needs its texts on the datasource too - see EmailActions in
// EmailActionModel.cs, which picks the confirmation shown afterwards.
const actions: Record<string, EmailAction> = {
    openHouseConfirmation: {
        mutation: "confirmOpenHouseWithToken",
        answerArg: "isJoining",
    },
    interestRegistration: {
        mutation: "registerInterestWithToken",
        answerArg: "interested",
    },
};

type ActionResult = {
    data: Record<string, boolean | null>;
};

function showState(root: HTMLElement, state: "success" | "error") {
    // A state with no copy on the datasource has no element - never leave the user on a
    // blank component, fall back to the error text.
    const wanted = root.querySelector(`[data-state="${state}"]`) ? state : "error";

    root.querySelectorAll<HTMLElement>("[data-state]").forEach((elm) => {
        elm.hidden = elm.dataset.state !== wanted;
    });
}

async function submit(
    root: HTMLElement,
    action: EmailAction,
    token: string,
    answer: boolean,
) {
    const query = `
      mutation EmailAction($token: String!, $answer: Boolean!) {
        ${action.mutation}(token: $token, ${action.answerArg}: $answer)
      }
    `;

    try {
        const result = await fetchApiGraphQL<ActionResult>(query, {
            token,
            answer,
        });
        // The tokens are single use, so a reload lands here with false or an error.
        showState(root, result?.data?.[action.mutation] ? "success" : "error");
    } catch (err) {
        console.log("Error while submitting the advert email action", err);
        showState(root, "error");
    }
}

export default function init() {
    createEnrichmentIsland(".email-action", (root: Element) => {
        const params = new URLSearchParams(document.location.search);
        const action = actions[params.get("action") ?? ""];
        const token = params.get("token");
        const answer = params.get("answer");

        if (!action || !token || (answer !== "true" && answer !== "false")) {
            showState(root as HTMLElement, "error");
            return;
        }

        void submit(root as HTMLElement, action, token, answer === "true");
    });
}
